import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';

// Serverless form delivery (works on static hosting — no backend to wake up).
// Get a free access key at https://web3forms.com (just enter your email), then set
// REACT_APP_WEB3FORMS_KEY in your host's env vars. Submissions are emailed to you.
const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY || '5b34c157-b3e6-48e7-b39d-b3f6ad427f83';

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service_interest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const resp = await axios.post('https://api.web3forms.com/submit', {
        access_key: WEB3FORMS_KEY,
        subject: `New 23rd Industries inquiry — ${formData.name || 'website'}`,
        from_name: '23rd Industries Website',
        ...formData,
      });
      if (!resp.data || !resp.data.success) {
        throw new Error(resp.data && resp.data.message ? resp.data.message : 'submit failed');
      }
      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service_interest: '',
        message: ''
      });
    } catch (error) {
      setSubmitError('Failed to submit form. Please try again or call us directly.');
      console.error('Contact form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="bg-black pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-24 bg-zinc-950 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="badge bg-orange-500/10 border border-orange-500/50 text-orange-500 px-4 py-2 mb-6 inline-block">
              Get In Touch
            </span>
            <h1 className="text-5xl sm:text-6xl md:text-7xl uppercase tracking-wider font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Contact Us
            </h1>
            <p className="text-zinc-300 text-lg leading-relaxed">
              Ready to experience the power of dry ice blasting? Schedule your free on-site demo 
              or request an estimate today.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 bg-black" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl uppercase tracking-wide font-bold text-white mb-8" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                Send Us a Message
              </h2>
              
              {submitSuccess ? (
                <div className="bg-orange-500/10 border border-orange-500/50 p-8 text-center" data-testid="success-message">
                  <CheckCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
                  <h3 className="text-2xl uppercase tracking-wide font-bold text-white mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                    Message Sent!
                  </h3>
                  <p className="text-zinc-300">
                    Thank you for reaching out. We'll get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => setSubmitSuccess(false)}
                    className="mt-6 btn-outline px-6 py-2"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form space-y-6" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-zinc-400 font-mono mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your Name"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-zinc-400 font-mono mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                        data-testid="input-email"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-zinc-400 font-mono mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                        data-testid="input-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-zinc-400 font-mono mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Company Name"
                        data-testid="input-company"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-zinc-400 font-mono mb-2">
                      Service Interest
                    </label>
                    <select
                      name="service_interest"
                      value={formData.service_interest}
                      onChange={handleChange}
                      data-testid="select-service"
                    >
                      <option value="">Select a service...</option>
                      <option value="dry_ice_blasting">Dry Ice Blasting</option>
                      <option value="fleet_cleaning">Fleet Cleaning</option>
                      <option value="fire_damage">Fire & Smoke Damage</option>
                      <option value="mold_remediation">Mold Remediation</option>
                      <option value="equipment_restoration">Equipment Restoration</option>
                      <option value="hvac_cleaning">HVAC Cleaning</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-zinc-400 font-mono mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      placeholder="Tell us about your cleaning needs..."
                      data-testid="textarea-message"
                    />
                  </div>
                  
                  {submitError && (
                    <div className="bg-red-500/10 border border-red-500/50 p-4 text-red-500 text-sm" data-testid="error-message">
                      {submitError}
                    </div>
                  )}
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full py-4 text-lg inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <Send className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl uppercase tracking-wide font-bold text-white mb-8" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                Contact Information
              </h2>
              
              <div className="space-y-6 mb-12">
                <a href="tel:8172109693" className="flex items-start gap-4 p-6 bg-zinc-950 border border-zinc-800 hover:border-orange-500 transition-colors group" data-testid="contact-phone">
                  <div className="w-12 h-12 bg-orange-600 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-widest text-zinc-400 font-mono">Phone</span>
                    <p className="text-white text-xl font-mono mt-1">(817) 210-9693</p>
                    <p className="text-zinc-400 text-sm mt-1">Local / Direct Line</p>
                  </div>
                </a>
                
                <a href="tel:8773528760" className="flex items-start gap-4 p-6 bg-zinc-950 border border-zinc-800 hover:border-orange-500 transition-colors group" data-testid="contact-tollfree">
                  <div className="w-12 h-12 bg-orange-600 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-widest text-zinc-400 font-mono">Toll-Free</span>
                    <p className="text-white text-xl font-mono mt-1">(877) 352-8760</p>
                    <p className="text-zinc-400 text-sm mt-1">Available nationwide</p>
                  </div>
                </a>
                
                <a href="mailto:operations@23rdindustries.com" className="flex items-start gap-4 p-6 bg-zinc-950 border border-zinc-800 hover:border-orange-500 transition-colors group" data-testid="contact-email">
                  <div className="w-12 h-12 bg-orange-600 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-widest text-zinc-400 font-mono">Email</span>
                    <p className="text-white text-lg font-mono mt-1">operations@23rdindustries.com</p>
                    <p className="text-zinc-400 text-sm mt-1">We respond within 24 hours</p>
                  </div>
                </a>
                
                <div className="flex items-start gap-4 p-6 bg-zinc-950 border border-zinc-800" data-testid="contact-location">
                  <div className="w-12 h-12 bg-orange-600 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-widest text-zinc-400 font-mono">Location</span>
                    <p className="text-white text-lg mt-1">Haltom City, TX</p>
                    <p className="text-zinc-400 text-sm mt-1">DFW Metroplex + 100mi Service Radius</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-6 bg-zinc-950 border border-zinc-800" data-testid="contact-hours">
                  <div className="w-12 h-12 bg-orange-600 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-widest text-zinc-400 font-mono">Operations</span>
                    <p className="text-white text-lg mt-1">24/7 Availability</p>
                    <p className="text-zinc-400 text-sm mt-1">Emergency services available</p>
                  </div>
                </div>
              </div>

              {/* Demo CTA */}
              <div className="bg-orange-600 p-8">
                <h3 className="text-2xl uppercase tracking-wide font-bold text-black mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  The 23rd Demo Offer
                </h3>
                <p className="text-black/80 mb-6">
                  Provide one truck or component. We demonstrate full cleaning results on-site 
                  to prove effectiveness before commitment.
                </p>
                <p className="text-black font-bold font-mono">
                  100% Free. No Obligation.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-24 bg-zinc-950 border-t border-zinc-800" data-testid="service-area">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="badge bg-zinc-900 border border-zinc-800 text-zinc-400 px-4 py-2 mb-6 inline-block">
              Service Area
            </span>
            <h2 className="text-4xl sm:text-5xl uppercase tracking-wide font-bold text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Dallas-Fort Worth & Beyond
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto mb-12">
              We serve the entire DFW Metroplex and surrounding areas within a 100-mile radius. 
              Mobile service capability means we come to you.
            </p>
            
            <div className="grid md:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {['Dallas', 'Fort Worth', 'Arlington', 'Plano', 'Irving', 'Denton', 'Haltom City', '+ Surrounding'].map((city, i) => (
                <div key={i} className="bg-black border border-zinc-800 p-4 text-center">
                  <span className="text-white font-mono">{city}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
